import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Icon from '../AppIcon';

const TOAST_EVENT = 'advika-toast';
const ToastContext = createContext(null);

const toneStyles = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  error: 'border-red-200 bg-red-50 text-red-900',
  info: 'border-[#DDD6FE] bg-[#F5F3FF] text-[#312E81]',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
};

const toneIcons = {
  success: 'CheckCircle2',
  error: 'CircleAlert',
  info: 'Info',
  warning: 'AlertTriangle',
};

const normalizeToast = (input) => {
  if (typeof input === 'string') return { message: input };
  return input || {};
};

export const notify = (input) => {
  window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail: normalizeToast(input) }));
};

export const toast = {
  success: (message, options = {}) => notify({ ...options, type: 'success', message }),
  error: (message, options = {}) => notify({ ...options, type: 'error', message }),
  info: (message, options = {}) => notify({ ...options, type: 'info', message }),
  warning: (message, options = {}) => notify({ ...options, type: 'warning', message }),
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) return toast;
  return context;
};

const ToastProvider = ({ children }) => {
  const [items, setItems] = useState([]);

  const dismiss = useCallback((id) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const push = useCallback((input) => {
    const toastInput = normalizeToast(input);
    if (!toastInput.message) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const item = {
      id,
      type: toastInput.type || 'info',
      message: toastInput.message,
      title: toastInput.title,
      duration: toastInput.duration ?? 4500,
    };
    setItems((current) => [item, ...current].slice(0, 4));
    if (item.duration > 0) {
      window.setTimeout(() => dismiss(id), item.duration);
    }
  }, [dismiss]);

  useEffect(() => {
    const handler = (event) => push(event.detail);
    window.addEventListener(TOAST_EVENT, handler);
    return () => window.removeEventListener(TOAST_EVENT, handler);
  }, [push]);

  const api = useMemo(() => ({
    notify: push,
    success: (message, options = {}) => push({ ...options, type: 'success', message }),
    error: (message, options = {}) => push({ ...options, type: 'error', message }),
    info: (message, options = {}) => push({ ...options, type: 'info', message }),
    warning: (message, options = {}) => push({ ...options, type: 'warning', message }),
  }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed right-4 top-4 z-[9999] flex w-[min(92vw,380px)] flex-col gap-3">
        {items.map((item) => (
          <div
            key={item.id}
            className={`pointer-events-auto flex gap-3 rounded-xl border p-4 shadow-[0_18px_55px_rgba(15,23,42,0.16)] backdrop-blur ${toneStyles[item.type] || toneStyles.info}`}
            role={item.type === 'error' ? 'alert' : 'status'}
          >
            <Icon name={toneIcons[item.type] || toneIcons.info} size={18} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              {item.title ? <p className="text-sm font-semibold">{item.title}</p> : null}
              <p className="text-sm leading-5">{item.message}</p>
            </div>
            <button
              type="button"
              onClick={() => dismiss(item.id)}
              className="shrink-0 rounded-md p-1 opacity-70 transition hover:bg-white/60 hover:opacity-100"
              aria-label="Dismiss notification"
            >
              <Icon name="X" size={15} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
